<template>
    <!-- *********************************************
    *                                                *
    *                  Full Screen Modal             *
    *                                                *
    ********************************************** -->
    <modals-side-generic
    v-if="fullScreenDialog && getMobileAppState"
    upBellowEntry
    buttonFullWidth
    preventDetailsModalClose
    :applySecondLayer="secondSideModalLayer"
    :helperTextTitle="helperTextTitle"
    :tabs="tabs"
    :title="title"
    :negativeTitle="negative"
    :showFooter="buttonsFooterData.length"
    :buttonsFooterData="buttonsFooterData"
    :triggerClose="triggerClose"
    :emitLeftButtonText="emitLeftButtonText"
    @allowPortalElementsToBeRender="emit('allowPortalElementsToBeRender', true)"
    @tabClicked="emit('tabClicked', $event)"
    @hideMe="emit('hideMe')"
    @backOrCloseClicked="emit('backOrCloseClicked')">
        <div class="full-screen-modal-content">
            <slot></slot>
        </div>
    </modals-side-generic>
    <!-- *********************************************
    *                                                *
    *                Center Default Modal            *
    *                                                *
    ********************************************** -->
    <teleport
    v-else
    :to="teleportOverCameraLayer ? '#modals-centered-over-camera-teleport' : secondModalLayer ? '#second-modals-centered-overlay-teleport' :   '#modals-centered-overlay-teleport'"
    :disabled="showOnParent">
        <!-- Dark Layer Transition -->
        <transition
        name="center-modal-animation"
        appear
        @after-leave="emit('hideMe')">
            <div
            v-if="showMe"
            class="overlay-modal-wrapper dont-close-side-modals"
            :class="{ 'session-expired': sessionExpired }">
                <div
                v-click-outside="closeModal"
                class="center-modal-wrapper-base"
                :class="{'mobile': getMobileAppState, 'content-with-no-padding': contentWithNoPadding}"
                :style="{'width': `${modalWidth}`, 'margin-bottom': getKeyboardHeight}">
                    <!-- *********************************************
                    *  	                   Header                	 *
                    ********************************************** -->
                    <div class="modal-header" :class="[{'negative': negative}, titleIconSize]">
                        <component :is="titleIcon"/>
                        {{title}}
                        <!-- Top Close Button -->
                        <button-tertiary
                        v-if="withTopCloseButton"
                        icon="close"
                        buttonStyle="round"
                        buttonSize="small"
                        @clicked="showMe = false"/>
                    </div>
                    <!-- *********************************************
                    *  	                   Content                	 *
                    ********************************************** -->
                    <div class="modal-content">
                        <slot></slot>
                    </div>
                    <!-- *********************************************
                    *  	                   Footer                	 *
                    ********************************************** -->
                    <div
                    v-if="!noFooterButtons"
                    class="modal-footer"
                    :class="{'negative': negative}"
                    :style="{'margin-top': footerMarginTop}">
                        <!-- If Single Button we will use primary button -->
                        <component
                        v-if="!rightButtonText"
                        buttonSize="medium"
                        iconPosition="right"
                        :is="negative ? ButtonNegative : ButtonPrimary"
                        :icon="leftButtonIcon"
                        :label="leftButtonText ? leftButtonText : t('modals.cancelBtn')"
                        @clicked="[showMe = false, emit('leftButtonClicked')]"/>
                        <!-- If two buttons we will use secondary -->
                        <button-secondary
                        v-else
                        :label="leftButtonText ? leftButtonText : t('modals.cancelBtn')"
                        @clicked="[showMe = false, emit('leftButtonClicked')]"/>
                        <!-- If Right Button -->
                        <button-primary
                        v-if="rightButtonText && !negative"
                        buttonSize="medium"
                        iconPosition="right"
                        :icon="rightButtonIcon"
                        :label="rightButtonText"
                        :disabled="rightButtonDisabled"
                        @clicked="emit('rightButtonClicked')"/>
                        <!-- If Right Button but negative variant -->
                        <button-negative
                        v-if="rightButtonText && negative"
                        iconPosition="right"
                        :icon="rightButtonIcon"
                        :label="rightButtonText"
                        :disabled="rightButtonDisabled"
                        @clicked="emit('rightButtonClicked')"/>
                    </div>
                    <!-- *********************************************
                    *               After Footer Content             *
                    ********************************************** -->
                    <div class="modal-after-footer-content">
                        <slot name="modal-after-footer-content"></slot>
                    </div>
                </div>
            </div>
        </transition>
    </teleport>
</template>

<script setup>
import ModalsSideGeneric from '@/components/generic/modals/side/Generic'
import ButtonNegative from '@/components/generic/buttons/Negative'
import ButtonPrimary from '@/components/generic/buttons/Primary'
import ButtonSecondary from '@/components/generic/buttons/Secondary'
import ButtonTertiary from '@/components/generic/buttons/Tertiary'
/* Vue & Assets */
import { ref, watch, computed, defineAsyncComponent, onBeforeUnmount } from 'vue'
import { validateIfParentExists } from '@/assets/scripts/htmlManipulators.js'
/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['hideMe', 'clicked', 'leftButtonClicked', 'rightButtonClicked', 'tabClicked', 'backOrCloseClicked', 'allowPortalElementsToBeRender'])

/* Props
@secondModalLayer {Boolean} - to target a superior layer for modal teleport
@secondSideModalLayer {Boolean} - prop to apply second-overlay-teleport styles
@sessionExpired {Boolean} - to force specific z-index for this modal
@preventClickOutsideClose {Boolean} - prevent click outside modal from closing it
@fullScreenDialog {Boolean} - prop to make dialog ocupy full screen size in mobile
@modalWidth {String} - value to apply specific width for modal
@footerMarginTop {String} - value to apply specific margin top for modal footer
@contentWithNoPadding {Boolean} - prop to remove padding from the wrapper
@title {String} - modal title
@titleIcon {String} - modal title icon
@titleIconSize {String} - [small(default), big]
@negative {Boolean} - prop to apply title & right button negative styles
@withTopCloseButton {Boolean} - to apply top close button
@noFooterButtons {Boolean}- prop to hide fotter buttons
@leftIconPosition {String} - icon for left button
@leftButtonText {String} - text for left button, this button has a default text if this is not passed
@rightButtonIcon {String} - icon for right button
@rightButtonText {String} - text for right button
@rightButtonDisabled {Boolean} - prop to make second right button disabled
@triggerClose {Boolean} - prop to trigger close of modal
@teleportOverCameraLayer {Boolean} - prop to teleport modal to '#modals-centered-over-camera-teleport'
@tabs {Array} - tabs to display on header
@helperTextTitle {Boolean} - prop to apply helper text title styles
*/
const props = defineProps({
    secondModalLayer: Boolean,
    secondSideModalLayer: Boolean,
    sessionExpired: Boolean,
    preventClickOutsideClose: Boolean,
    fullScreenDialog: Boolean,
    modalWidth: String,
    footerMarginTop: String,
    contentWithNoPadding: Boolean,
	title: String,
    titleIcon: String,
    titleIconSize: String,
    negative: Boolean,
    withTopCloseButton: Boolean,
    noFooterButtons: Boolean,
    leftButtonIcon: String,
    leftButtonText: String,
    rightButtonIcon: String,
    rightButtonText: String,
    rightButtonDisabled: Boolean,
    triggerClose: Boolean,
    teleportOverCameraLayer: Boolean,
    tabs: Array,
    helperTextTitle: Boolean,
    showOnParent: Boolean
})

const showMe = ref(true)

watch(() => props.triggerClose, () => { showMe.value = false })

const validateIfClose = $event => {
    const className = 'dont-close-centered-modals'
    const eventHasClass = $event.target.className && JSON.stringify($event.target.className).toLowerCase().includes(className)

    return validateIfParentExists($event.target, className) || eventHasClass
}

const closeModal = $event => {
    if (validateIfClose($event) || props.preventClickOutsideClose) return
    /* If not, then we close our sideModal */
    showMe.value = false
}

const { updatePreventMobileFeatureBack, updatePreventMobileSideModalBack } = mapActions()
const { getKeyboardHeight, getMobileAppState } = mapGetters()

/* Computed */
const titleIcon = computed(() => {
    const icon = props.negative && !props.titleIcon ? 'warning' : props.titleIcon
    if (!icon) return
	return defineAsyncComponent(() => import('@/assets/icons/' + icon + '.svg?inline'))
})

const buttonsFooterData = computed(() => {
    const buttons = []

    if (props.rightButtonText) {
        buttons.push({
            iconPosition: 'right',
            label: props.rightButtonText,
            buttonType: props.negative ? 'negative' : 'primary',
            icon: props.rightButtonIcon,
            disabled: props.rightButtonDisabled,
            callbackFunction: () => { emit('rightButtonClicked') }
        })
    }

    return buttons
})
/***********************************************************
*                                                          *
*                 MOBILE NATIVE BACK LOGIC                 *
*                                                          *
***********************************************************/
/* Preving mobile back to trigger features close */
updatePreventMobileFeatureBack(true)
updatePreventMobileSideModalBack(true)
onBeforeUnmount(() => {
	updatePreventMobileFeatureBack(false)
    updatePreventMobileSideModalBack(false)
})

const onBackKeyDown = () => {
    if (props.preventClickOutsideClose) return
    showMe.value = false
}

document.addEventListener('backbutton', onBackKeyDown, false)
</script>

<style lang="scss" scoped>
.full-screen-modal-content {
    width: 100%;
    padding: 2px 16px 0 16px;
    height: auto;

    display: flex;
    flex-direction: column;
    align-items: center;

    @include H6-Regular;
    color: var(--secondary-dark-2);
    letter-spacing: normal;
    line-height: 1.31;
}

.overlay-modal-wrapper {
    @include modalsCenteredOverlayTeleportStyles;
    @include centerModalAnimation;

    justify-content: center;
    align-items: center;

    .center-modal-wrapper-base {
        width: 448px;
        max-height: calc(100% - 48px);
        max-width: calc(100% - 40px);
        padding: 24px;

        display: flex;
        flex-direction: column;
        position: relative;

        border-radius: 8px;
        background-color: var(--secondary-light-4);
        box-shadow: 0px 6px 24px 0px rgba(0, 0, 0, 0.16);

        &.content-with-no-padding {
            padding: 24px 0;

            .modal-header {
                padding: 0 24px;
            }

            .modal-footer {
                padding: 0 24px;
            }
        }

        .modal-header {
            width: 100%;
            min-height: 28px;
            margin-bottom: 4px;

            display: flex;
            flex-direction: column;
            flex-shrink: 0;
            gap: 8px;

            @include H3-Medium;
            color: var(--primary);
            line-height: 28px;

            svg {
                width: 32px;
                height: 32px;

                :deep(.change-color-fill) {
                    fill: var(--primary);
                }
            }

            &.negative {
                color: var(--negative);

                svg :deep(.change-color-fill) {
                    fill: var(--negative);
                }
            }

            &.big {
                svg {
                    width: 88px;
                    height: 88px;

                    :deep(.change-color-fill) {
                        fill: var(--secondary-light-1);
                    }
                }
            }

            .tertiary-button-wrapper {
                position: absolute;
                right: 24px;
            }
        }

        .modal-content, .modal-after-footer-content {
            width: 100%;
            height: auto;

            display: flex;
            flex-direction: column;
            align-items: center;

            @include H6-Regular;
            color: var(--secondary-dark-2);
            letter-spacing: normal;
            line-height: 1.31;
        }

        .modal-footer {
            width: 100%;
            height: 48px;
            margin-top: 24px;

            display: flex;
            flex-shrink: 0;
            gap: 16px;

            button {
                width: 100%;
                flex: auto;
            }

            .negative-button-wrapper {
                width: 100%;
                flex: auto;

                :deep(.button-negative) {
                    width: 100%;
                }
            }
        }

        &.mobile {
            padding: 16px;

            &.content-with-no-padding {
                padding: 16px 0;

                .modal-header {
                    padding: 0 16px;
                }

                .modal-footer {
                    padding: 0 16px;
                    gap: 8px;
                }
            }

            .modal-footer {
                gap: 8px;

                .button-secondary, :deep(.button-negative) {
                    padding: 18px 8px;
                }
            }
        }
    }

    &.session-expired {
        z-index: 449;
    }
}
</style>
